import store from '~/components/myoddschecker/myoc.store';
import { type MyOCData } from '~/types/myoc';

import { $http, type IHttpOptions } from '../util/$http';

import RequestService from './requests-actions';
import SiteConfigActions, {
	type ISiteConfigActions,
} from './site-config-actions';
import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from './tracking-actions';

interface IUserData {
	data: {
		apiResponse?: {
			journey: string;
			message: string;
			success: boolean;
		};
		complete?: boolean;
		myoc: MyOCData;
	};
}

class MyOCActions {
	private baseUrl: string;
	private errors: any;
	private numErrors: number;
	private validations: any;
	private trackingActions: TrackingActions;
	private requestService = RequestService.getInstance();
	private siteConfigActions: ISiteConfigActions;

	private updateClient: (key: string, val: any) => void;

	emailHint: string;

	constructor(validations: any) {
		// Error Handling
		this.errors = [];
		this.numErrors = 0;
		this.validations = validations;

		this.baseUrl = store.getState().server.config.baseUrl;

		// Helper
		this.emailHint = '';

		this.trackingActions = new TrackingActions();
		this.siteConfigActions = new SiteConfigActions();

		this.updateClient = store.getState().updateClient;
	}

	public toggleSocialLogins({
		facebookEnabledLocalConfig,
		googleEnabledLocalConfig,
	}: {
		facebookEnabledLocalConfig?: boolean;
		googleEnabledLocalConfig?: boolean;
	}) {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(url.search);
		const facebookPermitted = params.get('disable-facebook-sso') !== 'true';
		const googlePermitted = params.get('disable-google-sso') !== 'true';

		const facebookEnabledSiteConfig = this.siteConfigActions.getSiteConfigVar(
			'myoc.show-facebook-login',
		);
		const googleEnabledSiteConfig = this.siteConfigActions.getSiteConfigVar(
			'myoc.show-google-login',
		);

		// NOTE: we are temporarily falling back to original gitlab implementation here,
		// via nullish coalescing, while we investigate issues with site-config proxy

		const facebookEnabled =
			facebookPermitted &&
			(facebookEnabledSiteConfig ?? facebookEnabledLocalConfig);
		const googleEnabled =
			googlePermitted && (googleEnabledSiteConfig ?? googleEnabledLocalConfig);

		window.ocAuth.toggleSocialLogins({ facebookEnabled, googleEnabled });
	}

	// Method to validate the field by mapping the passed validation string to the appropriate function.
	private getValidation(
		validation: string,
		param: any,
		value: string,
	): boolean {
		let valid = false;
		switch (validation) {
			case 'min':
				valid = this.min(param, value);
				break;
			case 'max':
				valid = this.max(param, value);
				break;
			case 'email':
				valid = this.email(param, value);
				break;
			default:
				validation = '';
				valid = false;
				break;
		}
		this.updateErrors(valid, validation);
		return valid;
	}

	// Method to update the errors array
	private updateErrors(condition: boolean, validation: string): void {
		if (validation !== '') {
			if (condition) {
				const index = this.errors.indexOf(validation);
				if (index >= 0) {
					this.errors.splice(index);
				}
				this.numErrors -= 1;
			} else if (!this.errors.includes(validation)) {
				this.errors.push(validation);
				this.numErrors += 1;
			}
		}
	}

	// Validation methods - add new ones below
	// Returning false in these methods indicates the validation has failed.
	private min(param: number, value: string): boolean {
		return value.length >= param;
	}

	private max(param: number, value: string): boolean {
		return value.length <= param;
	}

	private email(param: boolean, value: string): boolean {
		// Checking email through a regExp isn't 100% bulletproof, changed to this one as the previous one was too rigid
		const emailRegExp = /(.+)@(.+){2,}\.(.+){2,}/i;

		return emailRegExp.test(value) === param;
	}

	public canSubmit(): boolean {
		if (this.getNumErrors() > 1) {
			return false;
		}

		return true;
	}

	public validateField(value: any, passwordValue?: string) {
		Object.getOwnPropertyNames(this.validations).forEach(val => {
			this.getValidation(val, this.validations[val], value.trim());
		});

		// this only happens on the confirmPassword field
		if (
			typeof passwordValue === 'string' &&
			passwordValue !== value &&
			passwordValue !== '' &&
			value !== ''
		) {
			this.pushError('password-mismatch');
		}

		if (passwordValue === value) {
			this.clearErrors();
		}
	}

	public pushError(error: string): void {
		let errorFound = false;
		for (let i = 0; i < this.errors.length; i++) {
			if (this.errors[i] === error) {
				errorFound = true;
			}
		}

		if (!errorFound) {
			this.errors.push(error);
			this.numErrors += 1;
		}
	}

	public clearErrors(): void {
		this.numErrors = 0;
		this.errors = [];
	}

	public getNumErrors() {
		return this.numErrors;
	}

	/**
	 * Gets the errors for the specified field
	 *
	 * @param {string} field (Name of the field)
	 * @returns (Returns the error object for the specified field)
	 */
	public getFieldErrors() {
		return this.errors.shift();
	}

	public logout(
		endpoint = 'myoddschecker/logout',
		shouldRefreshPage = false,
	): any {
		const options: IHttpOptions = {
			method: 'POST',
			url: this.baseUrl + endpoint,
		};

		$http(options).finally(() => {
			this.trackingActions.eventTracking({
				event: TRACKING_EVENTS.LOG_OUT,
				eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.LOG_OUT],
			});

			window.dispatchEvent(new CustomEvent('OC:LogoutMyOC'));
			window.dispatchEvent(new CustomEvent('OC:InvalidateMyOC'));

			if (shouldRefreshPage) {
				window.location.reload();
			}
		});
	}

	public closeWindow(): void {
		window.history.back();
	}

	public getUserData(userToken: string): Promise<MyOCData> {
		return new Promise((resolve, reject) => {
			this.requestService
				.getUrl('myoddschecker/user', {
					token: userToken,
				})
				.then((res: IUserData) => {
					// delete things we shouldn't set in the tree
					delete res.data.apiResponse;
					delete res.data.complete;

					resolve(res.data.myoc);
				})
				.catch(e => {
					reject(e);
				});
		});
	}

	public newPoliciesAvailable(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			window.ocAuth
				.newPoliciesAvailable(res => {
					const acceptanceValues = Object.keys(res).map(function (e) {
						return res[e];
					});

					if (acceptanceValues.every(value => value)) {
						resolve(false);
					}
					resolve(true);
				})
				.catch(e => reject(e));
		});
	}
}

export default MyOCActions;
